/**
 * DowngradeNotice
 *
 * Shown when a user's subscription has ended or is about to end:
 *  - Trial expired: state === 'free' && trialEndsAt is in the past
 *  - Subscription cancelled: state === 'cancelled'
 *
 * Displayed as a dismissible banner above the page content (inside AppLayout).
 * Uses sessionStorage so it reappears on fresh sessions — a gentle recurring nudge.
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { X, StarOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSubscription } from '@/hooks/useSubscription'

const DISMISSED_KEY = 'shubh-downgrade-notice-dismissed'

type DowngradeReason = 'trial_expired' | 'cancelled'

function getDowngradeReason(
  state: string,
  trialEndsAt: string | null,
  cancelAtPeriodEnd: boolean,
): DowngradeReason | null {
  if (state === 'free' && trialEndsAt && new Date(trialEndsAt).getTime() < Date.now()) {
    return 'trial_expired'
  }
  // Cancelled but still in paid period — show notice so they know access is ending
  if (state === 'cancelled' || (state === 'active' && cancelAtPeriodEnd)) {
    return 'cancelled'
  }
  return null
}

export function DowngradeNotice() {
  const { subscription, isLoading } = useSubscription()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    setDismissed(!!sessionStorage.getItem(DISMISSED_KEY))
  }, [])

  if (isLoading || dismissed || !subscription) return null

  const reason = getDowngradeReason(
    subscription.state,
    subscription.trialEndsAt,
    subscription.cancelAtPeriodEnd,
  )

  if (!reason) return null

  const isTrialExpired = reason === 'trial_expired'

  const title = isTrialExpired
    ? 'Your free trial has ended'
    : 'Subscription cancelled'

  const body = isTrialExpired
    ? 'You\'re back on the free plan. Muhurta lookups and some features are now limited.'
    : subscription.nextBillingDate
    ? `Premium access ends ${new Date(subscription.nextBillingDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}. After that, you'll revert to the free plan.`
    : 'Your subscription will not renew. You\'ll revert to the free plan when access expires.'

  function dismiss() {
    sessionStorage.setItem(DISMISSED_KEY, '1')
    setDismissed(true)
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'relative z-40 w-full',
        'flex items-start gap-3 px-4 py-3',
        'border-b border-inauspicious/25 bg-inauspicious/8',
      )}
    >
      <div className="w-8 h-8 rounded-lg bg-inauspicious/15 border border-inauspicious/25 flex items-center justify-center flex-shrink-0 mt-0.5">
        <StarOff className="w-4 h-4 text-inauspicious" aria-hidden="true" />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-star-white text-sm font-semibold">{title}</p>
        <p className="text-star-soft text-xs mt-0.5 leading-relaxed">{body}</p>
        <div className="flex items-center gap-3 mt-2.5">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-violet to-violet-mid text-star-white hover:shadow-glow-violet transition-all"
          >
            {isTrialExpired ? 'Subscribe now' : 'Resubscribe'}
          </Link>
          <button
            onClick={dismiss}
            className="text-xs text-star-dim hover:text-star-soft transition-colors"
          >
            Remind me later
          </button>
        </div>
      </div>

      <button
        onClick={dismiss}
        aria-label="Dismiss notice"
        className="text-star-dim hover:text-star-soft transition-colors flex-shrink-0 mt-0.5"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
